import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Stack } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import * as datefns from 'date-fns'
import React, { useEffect, useState } from 'react'
import { UserCategoryType } from '@finance-lab/utils/types'
import { UserCategorySelect } from './UserCategorySelect'

export function defaultTrialPeriod() {
    const startsAt = datefns.startOfDay(new Date())
    const endsAt = datefns.startOfDay(datefns.addDays(startsAt, 30))
    return { startsAt, endsAt }
}

/** Calendar dates only: start of first day, end of last day (inclusive). */
export function trialPeriodToTimestamps(startDate: Date, endDate: Date) {
    return {
        startsAt: datefns.startOfDay(startDate),
        endsAt: datefns.endOfDay(endDate),
    }
}

export function UserCategoryDialog({
    open,
    onClose,
    userEmail,
    initialUserCategory,
    initialTrialPeriod,
    onSave,
}: {
    open: boolean
    onClose: () => void
    userEmail: string
    initialUserCategory: UserCategoryType
    initialTrialPeriod: { startsAt: string | Date; endsAt: string | Date } | null | undefined
    onSave: (userCategory: UserCategoryType, trialPeriod?: { startsAt: Date; endsAt: Date }) => Promise<void>
}) {
    const [userCategory, setUserCategory] = useState<UserCategoryType>(
        (initialUserCategory as UserCategoryType) || 'standard',
    )
    const [startsAt, setStartsAt] = useState<Date>(() => defaultTrialPeriod().startsAt)
    const [endsAt, setEndsAt] = useState<Date>(() => defaultTrialPeriod().endsAt)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        if (!open) return
        setUserCategory((initialUserCategory as UserCategoryType) || 'standard')
        if (initialTrialPeriod) {
            setStartsAt(datefns.startOfDay(new Date(initialTrialPeriod.startsAt)))
            setEndsAt(datefns.startOfDay(new Date(initialTrialPeriod.endsAt)))
        } else {
            const defaults = defaultTrialPeriod()
            setStartsAt(defaults.startsAt)
            setEndsAt(defaults.endsAt)
        }
    }, [open, initialUserCategory, initialTrialPeriod])

    const handleSave = async () => {
        setSaving(true)
        try {
            if (userCategory === 'trial') {
                if (datefns.startOfDay(endsAt).getTime() < datefns.startOfDay(startsAt).getTime()) {
                    alert('Slutdatum måste vara samma dag som eller efter startdatum.')
                    return
                }
                await onSave(userCategory, trialPeriodToTimestamps(startsAt, endsAt))
            } else {
                await onSave(userCategory)
            }
            onClose()
        } catch {
            alert('Kunde inte spara kategori.')
        } finally {
            setSaving(false)
        }
    }

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Ange kategori för {userEmail}</DialogTitle>
            <DialogContent>
                <FormControl fullWidth sx={{ my: 2 }}>
                    <UserCategorySelect value={userCategory} onChange={(category) => setUserCategory(category)} />
                </FormControl>
                {userCategory === 'trial' && (
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <DatePicker
                            label='Testperiod start'
                            value={startsAt}
                            onChange={(d) => d && setStartsAt(datefns.startOfDay(d))}
                        />
                        <DatePicker
                            label='Testperiod slut'
                            value={endsAt}
                            onChange={(d) => d && setEndsAt(datefns.startOfDay(d))}
                        />
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color='secondary' disabled={saving}>
                    Avbryt
                </Button>
                <Button onClick={() => void handleSave()} color='primary' disabled={saving}>
                    Spara
                </Button>
            </DialogActions>
        </Dialog>
    )
}
